import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';

gsap.registerPlugin(ScrollTrigger);

const HeroStory = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const goldLineRef = useRef(null);
  const productRef = useRef(null);
  const scrollIndicatorRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Gold line entrance
      gsap.from(goldLineRef.current, {
        scaleX: 0,
        transformOrigin: 'left center',
        duration: 1.2,
        ease: 'power4.inOut',
        delay: 0.3,
      });

      // Split text animation with enhanced reveal effect
      if (titleRef.current) {
        const split = new SplitType(titleRef.current, { types: 'lines,words,chars' });

        // Animate each character with a staggered reveal
        gsap.from(split.chars, {
          y: 120,
          opacity: 0,
          rotateX: -90,
          stagger: 0.02,
          duration: 1.2,
          ease: 'power4.out',
          delay: 0.5,
        });
      }

      // Subtitle fade with slide
      gsap.from(subtitleRef.current, {
        y: 40,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        delay: 1.4,
      });

      // Jar entrance (after subtitle)
      gsap.from(productRef.current, {
        opacity: 0,
        duration: 1.5,
        ease: 'power3.out',
        delay: 2.2, // Starts after subtitle settles
      });


      // Scroll indicator entrance
      gsap.from(scrollIndicatorRef.current, {
        opacity: 0,
        y: 20,
        duration: 0.8,
        ease: 'power2.out',
        delay: 2.2,
      });

      // Scroll indicator pulse animation
      gsap.to(scrollIndicatorRef.current.querySelector('.scroll-dot'), {
        y: 12,
        duration: 1.5,
        ease: 'power2.inOut',
        repeat: -1,
        yoyo: true,
        delay: 2.5,
      });

      // Background parallax
      gsap.to(heroRef.current, {
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
        backgroundColor: '#F5E6C8',
        ease: 'none',
      });


    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ backgroundColor: 'var(--atelier-cream)' }}
    >
      {/* Gold accent line */}
      <div
        ref={goldLineRef}
        className="absolute top-28 left-0 h-px bg-gradient-to-r from-transparent via-[var(--atelier-gold)] to-transparent"
        style={{ width: '100%' }}
      />

      <div className="text-center z-10 px-8">
        {/* Main title */}
        <h1
          ref={titleRef}
          className="text-6xl md:text-8xl lg:text-9xl font-light mb-12"
          style={{
            color: 'var(--atelier-black)',
            lineHeight: 1.1,
            perspective: '1000px',
          }}
        >
          VAKMANSCHAP.<br />
          HERUITGEVONDEN.
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="text-lg md:text-xl tracking-widest"
          style={{ color: 'var(--atelier-grey)' }}
        >
          Penotti Atelier. Waar traditie en verfijning samenkomen.
        </p>
      </div>

      {/* Product teaser - positioned on the right, peeking in */}
      <div
        ref={productRef}
        className="absolute top-1/2 right-0 transform translate-x-[40%] -translate-y-1/2 pointer-events-none"
        style={{
          width: '80vw',
          height: '160vh',
          zIndex: 5,
        }}
      >
        <img
          src="/images/duo-signatur-hero.png"
          alt="Penotti Atelier Duo Signatur"
          className="w-full h-full object-contain object-right"
          style={{
            filter: 'drop-shadow(-20px 20px 40px rgba(0,0,0,0.2))',
          }}
        />
      </div>

      {/* Enhanced Scroll indicator */}
      <div
        ref={scrollIndicatorRef}
        className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-4"
        style={{ zIndex: 10 }}
      >
        <span
          className="text-xs tracking-[0.3em] uppercase"
          style={{ color: 'var(--atelier-gold)' }}
        >
          Ontdek
        </span>
        <div
          className="w-8 h-14 border-2 rounded-full flex justify-center pt-2 relative overflow-hidden"
          style={{ borderColor: 'var(--atelier-gold)' }}
        >
          <div
            className="scroll-dot w-1.5 h-3 rounded-full"
            style={{ backgroundColor: 'var(--atelier-gold)' }}
          />
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--atelier-gold)"
          strokeWidth="2"
          className="animate-bounce"
        >
          <path d="M12 5v14M19 12l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
};

export default HeroStory;